import { SummarizeOutlined } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { ChatMessageBubble } from "../../../components/UIToolkit/Chat/ChatMessageBubble";
import { EyebrowLabel } from "../../../components/UIToolkit/molecules/EyebrowLabel";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { FONT_SIZE, INK } from "../../../theme/tokens";
import { BUBBLE_MAX_WIDTH, BUBBLE_SX } from "./consts";
import type { SummaryQuestionAnswer } from "./customPrintoutDraftState";

type Props = {
	/** Annie's one line on the summary sheet she would design. */
	summary: string;
	answer: SummaryQuestionAnswer;
	/** Annie is designing: the question can't be answered until she's done. */
	disabled: boolean;
	onAnswer: (accepted: boolean) => void;
	dataTestId: string;
};

/** What the user chose, once they have; asking something else instead leaves no note. */
const ANSWERED_KEY: Partial<Record<SummaryQuestionAnswer, string>> = {
	accepted: "printout.custom.ai.summary.question.accepted",
	declined: "printout.custom.ai.summary.question.declined",
};

/**
 * Annie's question when a request reads as a summary: the sheet she would make, what a summary gives up (it doesn't
 * list each person, so printing doesn't check that everyone is on the page), and the two answers. Yes designs it as
 * a summary; no designs nothing, and the user can ask again in other words.
 */
export const CustomPrintoutSummaryQuestion: FC<Props> = ({ summary, answer, disabled, onAnswer, dataTestId }) => {
	const { t } = useTranslation();
	const answeredKey = ANSWERED_KEY[answer];
	return (
		<ChatMessageBubble sender="assistant" maxWidth={BUBBLE_MAX_WIDTH} sx={BUBBLE_SX} dataTestId={dataTestId}>
			<EyebrowLabel sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.25, color: "inherit" }}>
				<SummarizeOutlined sx={{ fontSize: "inherit" }} />
				{t("printout.custom.ai.summary.question.title")}
			</EyebrowLabel>
			{summary}
			<Typography sx={{ mt: 0.5, fontSize: FONT_SIZE.CAPTION, color: INK.SECONDARY }}>{t("printout.custom.ai.summary.question.body")}</Typography>
			{answer === "open" && (
				<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
					<SystemButton
						buttonVariant="primary"
						size="compact"
						noFullWidthMobile
						disabled={disabled}
						onClick={() => onAnswer(true)}
						data-testid={`${dataTestId}-accept`}
					>
						{t("printout.custom.ai.summary.question.yes")}
					</SystemButton>
					<SystemButton
						buttonVariant="borderless"
						size="compact"
						noFullWidthMobile
						disabled={disabled}
						onClick={() => onAnswer(false)}
						data-testid={`${dataTestId}-decline`}
					>
						{t("printout.custom.ai.summary.question.no")}
					</SystemButton>
				</Box>
			)}
			{answeredKey !== undefined && (
				<Typography data-testid={`${dataTestId}-answer`} sx={{ mt: 0.5, fontSize: FONT_SIZE.CAPTION, color: INK.MUTED }}>
					{t(answeredKey)}
				</Typography>
			)}
		</ChatMessageBubble>
	);
};
